import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Card, CardContent, Divider, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, IconButton, InputLabel, Link, MenuItem, Select, Snackbar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import TableViewOutlinedIcon from "@mui/icons-material/TableViewOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Autocomplete from "@mui/material/Autocomplete";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import { getData, postData } from "../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../services/endpoints";

const monthOptions = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

const DIRECT_BACKEND_REPORTS = [
    { name: "TDS Report", endpoint: "tds-report" },
    { name: "PF Report", endpoint: "pf-report" },
    { name: "ECR File", endpoint: "ecr-file" },
    { name: "ESI Report", endpoint: "esi-report" },
    { name: "MC File", endpoint: "mc-file" },
    { name: "Profession Tax Report", endpoint: "pt-report" },
    { name: "Bank Report", endpoint: "bank-report" },
    { name: "Cash Report", endpoint: "cash-report" },
    { name: "Cash Requirements Report", endpoint: "cash-requirements" },
    { name: "Allowances Report", endpoint: "allowances-report" },
    { name: "Deductions Report", endpoint: "deductions-report" },
    { name: "YTD Summary", endpoint: "ytd-summary" },
    { name: "Direct Deposit Report", endpoint: "direct-deposit" },
    { name: "Payslip Report", endpoint: "payslip-report" },
];

const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    return [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];
};

const emptyFilters = {
    payrollMonth: "",
    payrollYear: "",
    department: "",
    location: "",
    employee: null,
    payrollBatch: "",
};

const normalize = (value = "") => value.toLowerCase().replace(/[^a-z0-9]/g, "");

const createFileName = (reportName, extension) => {
    const safeName = reportName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    return `${safeName || "payroll-report"}-${timestamp}.${extension}`;
};

const shouldHideColumn = (columnName = "") => {
    const normalized = String(columnName).toLowerCase().replace(/[^a-z0-9]/g, "");
    return normalized === "employeepersonalid";
};

const formatColumnLabel = (columnName = "") =>
    String(columnName)
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/_/g, " ")
        .trim()
        .replace(/\b\w/g, (ch) => ch.toUpperCase());

const extractRows = (responseData) => {
    const result = responseData?.result;
    if (Array.isArray(result?.data)) return result.data;
    if (Array.isArray(result)) return result;
    if (Array.isArray(result?.rows)) return result.rows;
    return [];
};

const resolveEndpointFromName = (reportName = "") => {
    const key = normalize(reportName);

    if (key.includes("tds")) return "tds-report";
    if (key.includes("providentfund") || key === "pfreport" || key.includes("pf"))
        return "pf-report";
    if (key.includes("esireport") || key === "esi") return "esi-report";
    if (key.includes("professionaltax") || key === "ptreport" || key.includes("pt"))
        return "pt-report";
    if (key.includes("bank")) return "bank-report";
    if (key === "cashreport" || key.includes("cashreport")) return "cash-report";
    if (key.includes("cashrequirements")) return "cash-requirements";
    if (key.includes("allowance")) return "allowances-report";
    if (key.includes("deduction")) return "deductions-report";
    if (key.includes("ytd") || key.includes("yeartodate")) return "ytd-summary";
    if (key.includes("directdeposit")) return "direct-deposit";
    if (key.includes("ecr")) return "ecr-file";
    if (key.includes("mcfile") || key === "mc") return "mc-file";
    if (key.includes("payslip") || key.includes("salaryregister") || key === "salaryreport")
        return "payslip-report";

    return null;
};

const mergeBackendReports = (dropdownReports = []) => {
    const reportMap = new Map();

    DIRECT_BACKEND_REPORTS.forEach((report) => {
        reportMap.set(report.endpoint, { ...report, reportTypeId: null });
    });

    dropdownReports.forEach((item) => {
        if (!item?.name) return;

        const endpoint = resolveEndpointFromName(item.name);
        const key = endpoint || normalize(item.name);
        const existing = reportMap.get(key);

        if (existing) {
            reportMap.set(key, {
                ...existing,
                reportTypeId: existing.reportTypeId || item.id || null,
            });
            return;
        }

        reportMap.set(key, {
            name: item.name,
            endpoint,
            reportTypeId: item.id || null,
        });
    });

    return Array.from(reportMap.values());
};

function ReportSectionCard({ title, reports, onClick }) {
    return (
        <Card
            sx={{
                height: "100%",
                borderRadius: 3,
                border: "1px solid #e3e8ef",
                boxShadow: "0 10px 30px rgba(15,23,42,0.06)",
            }}
        >
            <CardContent sx={{ p: 3 }}>
                <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{ mb: 2, color: "#0f172a", display: "flex", alignItems: "center", gap: 1 }}
                >
                    <AssessmentOutlinedIcon fontSize="small" />
                    {title}
                </Typography>

                <Grid container spacing={1.25}>
                    {reports.map((report) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={`${report.endpoint || "report"}-${report.name}`}>
                            <Link
                                component="button"
                                underline="none"
                                onClick={() => onClick(report)}
                                sx={{
                                    width: "100%",
                                    textAlign: "left",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    px: 1.5,
                                    py: 1,
                                    borderRadius: 1.5,
                                    color: "#1e293b",
                                    fontSize: 14,
                                    lineHeight: 1.4,
                                    border: "1px solid transparent",
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                        bgcolor: "#f8fafc",
                                        borderColor: "#dbeafe",
                                        color: "#0f172a",
                                    },
                                }}
                            >
                                <DescriptionOutlinedIcon sx={{ fontSize: 18, color: "#475569" }} />
                                {report.name}
                            </Link>
                        </Grid>
                    ))}
                </Grid>
            </CardContent>
        </Card>
    );
}

function PayslipReportsTab() {
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const hrId = userData?.id;

    const [selectedReport, setSelectedReport] = useState(null);
    const [filterOpen, setFilterOpen] = useState(false);
    const [filters, setFilters] = useState(emptyFilters);

    const [departments, setDepartments] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [payrollBatches, setPayrollBatches] = useState([]);
    const [backendReports, setBackendReports] = useState(DIRECT_BACKEND_REPORTS);
    const [loadingFilters, setLoadingFilters] = useState(false);
    const [activeAction, setActiveAction] = useState("");
    const [previewData, setPreviewData] = useState(null);

    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

    const years = useMemo(() => getYearOptions(), []);

    useEffect(() => {
        const loadFilterData = async () => {
            setLoadingFilters(true);
            try {
                const [departmentRes, employeeRes, monthRes, reportRes] = await Promise.allSettled([
                    getData(API_ENDPOINTS.getAllDepartments),
                    getData(API_ENDPOINTS.getPayrollsByHrId(hrId)),
                    getData(API_ENDPOINTS.getPayrollMonthsDropdown),
                    getData(API_ENDPOINTS.getPayrollReportTypesDropdown),
                ]);

                if (departmentRes.status === "fulfilled") {
                    const departmentList =
                        departmentRes.value?.message?.departments ||
                        departmentRes.value?.result?.departments ||
                        departmentRes.value?.result ||
                        [];
                    setDepartments(Array.isArray(departmentList) ? departmentList : []);
                }

                if (employeeRes.status === "fulfilled") {
                    const employeeList = Array.isArray(employeeRes.value?.result)
                        ? employeeRes.value.result
                        : [];
                    setEmployees(employeeList);
                }

                if (monthRes.status === "fulfilled") {
                    const batches = Array.isArray(monthRes.value?.result?.months)
                        ? monthRes.value.result.months.map((month) => ({
                            label: month,
                            value: month,
                        }))
                        : [];
                    setPayrollBatches(batches);
                }

                const dropdownReports =
                    reportRes.status === "fulfilled" && Array.isArray(reportRes.value?.result)
                        ? reportRes.value.result
                        : [];
                setBackendReports(mergeBackendReports(dropdownReports));
            } catch (error) {
                return null;
            } finally {
                setLoadingFilters(false);
            }
        };

        loadFilterData();
    }, [hrId]);

    const locationOptions = useMemo(() => {
        const set = new Set();
        employees.forEach((emp) => {
            const location =
                emp?.location || emp?.locationName || emp?.workLocation || emp?.branch || "";
            if (location) set.add(location);
        });
        return Array.from(set);
    }, [employees]);

    const filteredEmployees = useMemo(() => {
        if (!filters.department) return employees;

        const selectedDepartmentName =
            departments.find((d) => String(d.id) === String(filters.department))?.department || "";

        return employees.filter((emp) => {
            const empDepartment = emp?.department || emp?.departmentName || "";
            return empDepartment === selectedDepartmentName;
        });
    }, [departments, employees, filters.department]);

    const previewMeta = previewData?.result?.meta || {};
    const previewTotals = previewData?.result?.totals || {};
    const previewRows = useMemo(() => extractRows(previewData), [previewData]);
    const previewColumns = useMemo(() => {
        if (!previewRows.length) return [];
        const keys = new Set();
        previewRows.forEach((row) => {
            Object.keys(row || {}).forEach((key) => keys.add(key));
        });
        return Array.from(keys).filter((key) => !shouldHideColumn(key));
    }, [previewRows]);

    const formatPreviewValue = (value) => {
        if (value === null || value === undefined || value === "") return "-";
        if (typeof value === "object") return JSON.stringify(value);
        return String(value);
    };

    const openFilterDialog = (report) => {
        setSelectedReport(report);
        setFilters(emptyFilters);
        setPreviewData(null);
        setFilterOpen(true);
    };

    const closeFilterDialog = () => {
        setFilterOpen(false);
        setSelectedReport(null);
        setPreviewData(null);
    };

    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
    };

    const resolveMonthAndYear = () => {
        let month = filters.payrollMonth;
        let year = filters.payrollYear ? Number(filters.payrollYear) : null;

        if ((!month || !year) && filters.payrollBatch) {
            const parts = String(filters.payrollBatch).trim().split(/\s+/);
            if (parts.length >= 2) {
                if (!month) month = parts[0];
                if (!year) {
                    const parsedYear = Number(parts[parts.length - 1]);
                    year = Number.isFinite(parsedYear) ? parsedYear : null;
                }
            }
        }

        return { month, year };
    };

    const buildPayload = (report) => {
        const { month, year } = resolveMonthAndYear();
        const payload = {};

        if (filters.department) payload.departmentId = filters.department;

        const employeePersonalId =
            filters.employee?.id ||
            filters.employee?.employeePersonalId ||
            filters.employee?.user_id ||
            null;
        if (employeePersonalId) payload.employeePersonalId = employeePersonalId;

        const isYtdReport = report?.endpoint === "ytd-summary";
        if (isYtdReport) {
            if (!year) throw new Error("Payroll year is required for YTD Summary report.");
            payload.year = Number(year);
            return payload;
        }

        if (!year) {
            throw new Error("Payroll year is required.");
        }

        if (month) {
            payload.periodType = "monthly";
            payload.month = month;
            payload.year = Number(year);
            return payload;
        }

        payload.periodType = "annually";
        payload.year = Number(year);
        return payload;
    };

    const fetchReportData = async (report) => {
        if (!report) throw new Error("Please select a report.");

        const payload = buildPayload(report);

        if (report.endpoint) {
            return await postData(API_ENDPOINTS.getPayrollReportByType(report.endpoint), payload);
        }

        if (report.reportTypeId) {
            return await postData(API_ENDPOINTS.getPayrollReportByType(report.reportTypeId), payload);
        }

        throw new Error(`No API mapping found for "${report.name}".`);
    };

    const exportToExcel = (responseData, reportName) => {
        const rows = extractRows(responseData);
        if (!rows.length) throw new Error("No rows available to download.");

        const workbook = XLSX.utils.book_new();
        const reportSheet = XLSX.utils.json_to_sheet(rows);
        XLSX.utils.book_append_sheet(workbook, reportSheet, "Report");

        if (responseData?.result?.totals && typeof responseData.result.totals === "object") {
            const totalsRows = Object.entries(responseData.result.totals).map(([metric, value]) => ({
                metric,
                value,
            }));
            const totalsSheet = XLSX.utils.json_to_sheet(totalsRows);
            XLSX.utils.book_append_sheet(workbook, totalsSheet, "Totals");
        }

        if (responseData?.result?.meta && typeof responseData.result.meta === "object") {
            const metaRows = Object.entries(responseData.result.meta).map(([field, value]) => ({
                field,
                value:
                    typeof value === "string" || typeof value === "number" || typeof value === "boolean"
                        ? value
                        : JSON.stringify(value),
            }));
            const metaSheet = XLSX.utils.json_to_sheet(metaRows);
            XLSX.utils.book_append_sheet(workbook, metaSheet, "Meta");
        }

        XLSX.writeFile(workbook, createFileName(reportName, "xlsx"));
    };

    const exportToPdf = (responseData, reportName) => {
        const rows = extractRows(responseData);
        const meta = responseData?.result?.meta || {};
        const totals = responseData?.result?.totals || {};
        const columns = rows.length
            ? Array.from(
                rows.reduce((set, row) => {
                    Object.keys(row || {}).forEach((key) => set.add(key));
                    return set;
                }, new Set()),
            ).filter((key) => !shouldHideColumn(key))
            : [];

        const doc = new jsPDF("l", "mm", "a4");
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const marginX = 10;
        const marginY = 10;
        const contentWidth = pageWidth - marginX * 2;
        let y = marginY;

        const ensureSpace = (neededHeight = 8) => {
            if (y + neededHeight <= pageHeight - marginY) return;
            doc.addPage();
            y = marginY;
        };

        const drawTitle = (text) => {
            ensureSpace(9);
            doc.setFont("helvetica", "bold");
            doc.setFontSize(13);
            doc.text(text, marginX, y);
            y += 7;
        };

        const drawSubText = (text) => {
            ensureSpace(6);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);
            doc.text(text, marginX, y);
            y += 5;
        };

        const drawSectionHeader = (text) => {
            ensureSpace(8);
            doc.setFont("helvetica", "bold");
            doc.setFontSize(11);
            doc.text(text, marginX, y);
            y += 6;
        };

        const drawKeyValueBlock = (dataObj) => {
            const entries = Object.entries(dataObj || {});
            if (!entries.length) {
                drawSubText("No data available.");
                return;
            }

            const leftWidth = contentWidth / 2 - 2;
            const rightX = marginX + contentWidth / 2 + 2;
            const rowHeight = 6;
            let localY = y;

            for (let i = 0; i < entries.length; i += 2) {
                ensureSpace(rowHeight + 1);
                const [leftKey, leftValue] = entries[i];
                const right = entries[i + 1];

                doc.setFont("helvetica", "bold");
                doc.setFontSize(9);
                doc.text(`${leftKey}:`, marginX, localY);
                doc.setFont("helvetica", "normal");
                doc.text(
                    doc.splitTextToSize(formatPreviewValue(leftValue), leftWidth - 32),
                    marginX + 30,
                    localY,
                );

                if (right) {
                    const [rightKey, rightValue] = right;
                    doc.setFont("helvetica", "bold");
                    doc.text(`${rightKey}:`, rightX, localY);
                    doc.setFont("helvetica", "normal");
                    doc.text(
                        doc.splitTextToSize(formatPreviewValue(rightValue), leftWidth - 32),
                        rightX + 30,
                        localY,
                    );
                }

                localY += rowHeight;
                y = localY;
            }
        };

        const drawDataTable = () => {
            ensureSpace(12);
            drawSectionHeader(`Data (${rows.length} rows)`);
            if (!rows.length || !columns.length) {
                drawSubText("No data available for selected filters.");
                return;
            }

            const headerHeight = 7;
            const rowHeight = 7;
            const priorityKeys = [
                "emp_id",
                "employeeId",
                "employeeCode",
                "employee_name",
                "employeeName",
                "name",
                "department",
                "designation",
                "month",
                "year",
                "grossSalary",
                "grossEarningsMonthly",
                "basicPay",
                "netSalary",
                "netSalaryMonthly",
                "tds",
                "incomeTax",
            ];
            const normalizedPriority = priorityKeys.map((k) => normalize(k));
            const columnsByPriority = columns
                .slice()
                .sort((a, b) => {
                    const aIdx = normalizedPriority.indexOf(normalize(String(a)));
                    const bIdx = normalizedPriority.indexOf(normalize(String(b)));
                    const rankA = aIdx === -1 ? 999 : aIdx;
                    const rankB = bIdx === -1 ? 999 : bIdx;
                    return rankA - rankB;
                });
            const visibleColumns = columnsByPriority.slice(0, Math.min(6, columnsByPriority.length));
            const colWidth = contentWidth / visibleColumns.length;

            ensureSpace(headerHeight + rowHeight);
            doc.setFillColor(241, 245, 249);
            doc.rect(marginX, y, contentWidth, headerHeight, "F");
            doc.setFont("helvetica", "bold");
            doc.setFontSize(9);

            visibleColumns.forEach((col, index) => {
                const x = marginX + index * colWidth + 1.5;
                const label = formatColumnLabel(String(col)).slice(0, 22);
                doc.text(label, x, y + 4.7);
            });

            y += headerHeight;

            doc.setFont("helvetica", "normal");
            doc.setFontSize(8.5);
            rows.forEach((row, rowIndex) => {
                ensureSpace(rowHeight);
                if (rowIndex % 2 === 1) {
                    doc.setFillColor(248, 250, 252);
                    doc.rect(marginX, y, contentWidth, rowHeight, "F");
                }

                visibleColumns.forEach((col, colIndex) => {
                    const x = marginX + colIndex * colWidth + 1.5;
                    const value = formatPreviewValue(row?.[col]).slice(0, 32);
                    doc.text(value, x, y + 4.7);
                });

                y += rowHeight;
            });
        };

        drawTitle(reportName);
        drawSubText(`Generated at: ${new Date().toLocaleString()}`);
        drawSubText(`Rows: ${rows.length}`);
        y += 2;

        drawSectionHeader("Meta");
        drawKeyValueBlock(meta);
        y += 2;

        drawSectionHeader("Totals");
        drawKeyValueBlock(totals);
        y += 2;

        drawDataTable();
        doc.save(createFileName(reportName, "pdf"));
    };

    const runReportAction = async (action) => {
        if (!selectedReport) return;

        setActiveAction(action);
        try {
            if (action === "preview") {
                const response = await fetchReportData(selectedReport);
                const isFailed = response?.statusCode && response.statusCode !== 200;

                if (response?.success === false) {
                    throw new Error(
                        response?.error?.message || response?.error || "Failed to fetch report.",
                    );
                }

                if (isFailed || response?.status === "FAILED") {
                    throw new Error(response?.message || "Failed to fetch report.");
                }

                const rows = extractRows(response);
                if (!rows.length) {
                    setPreviewData(null);
                    setSnackbar({
                        open: true,
                        severity: "warning",
                        message: "No data available for selected filters.",
                    });
                    return;
                }
                setPreviewData(response);
                setSnackbar({
                    open: true,
                    severity: "success",
                    message: `${selectedReport.name} preview loaded (${rows.length} rows).`,
                });
                return;
            }

            if (!previewData) {
                throw new Error("Please preview the report before downloading.");
            }

            if (action === "excel") {
                exportToExcel(previewData, selectedReport.name);
                setSnackbar({
                    open: true,
                    severity: "success",
                    message: `${selectedReport.name} downloaded as Excel.`,
                });
                return;
            }

            if (action === "pdf") {
                exportToPdf(previewData, selectedReport.name);
                setSnackbar({
                    open: true,
                    severity: "success",
                    message: `${selectedReport.name} downloaded as PDF.`,
                });
            }
        } catch (error) {
            setSnackbar({
                open: true,
                severity: "error",
                message: error.message || "Unable to complete report action.",
            });
        } finally {
            setActiveAction("");
        }
    };

    return (
        <Box>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h5" fontWeight={800} sx={{ color: "#0f172a", mb: 0.5 }}>
                    Reports
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Select a report type, apply filters, preview data, then download.
                </Typography>
            </Box>

            <Grid container spacing={2.5}>
                <Grid size={{ xs: 12 }}>
                    <ReportSectionCard
                        title="Payroll Reports"
                        reports={backendReports}
                        onClick={openFilterDialog}
                    />
                </Grid>
            </Grid>

            <Dialog open={filterOpen} onClose={closeFilterDialog} fullWidth maxWidth="md">
                <DialogTitle sx={{ pb: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {previewData && (
                            <IconButton
                                size="small"
                                onClick={() => setPreviewData(null)}
                                aria-label="Back to filters"
                            >
                                <ArrowBackIcon fontSize="small" />
                            </IconButton>
                        )}
                        <Box>
                            <Typography fontWeight={700}>
                                {previewData ? "Report Preview" : "Report Filters"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {selectedReport?.name || "Select report"}
                            </Typography>
                        </Box>
                    </Box>
                </DialogTitle>

                <DialogContent dividers>
                    {!previewData || previewRows.length === 0 ? (
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Payroll Month</InputLabel>
                                    <Select
                                        label="Payroll Month"
                                        value={filters.payrollMonth}
                                        onChange={(e) => handleFilterChange("payrollMonth", e.target.value)}
                                    >
                                        {monthOptions.map((month) => (
                                            <MenuItem key={month} value={month}>
                                                {month}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Payroll Year</InputLabel>
                                    <Select
                                        label="Payroll Year"
                                        value={filters.payrollYear}
                                        onChange={(e) => handleFilterChange("payrollYear", e.target.value)}
                                    >
                                        {years.map((year) => (
                                            <MenuItem key={year} value={year}>
                                                {year}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Department</InputLabel>
                                    <Select
                                        label="Department"
                                        value={filters.department}
                                        onChange={(e) => handleFilterChange("department", e.target.value)}
                                    >
                                        <MenuItem value="">All Departments</MenuItem>
                                        {departments.map((dept) => (
                                            <MenuItem key={dept.id} value={String(dept.id)}>
                                                {dept.department}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Location</InputLabel>
                                    <Select
                                        label="Location"
                                        value={filters.location}
                                        onChange={(e) => handleFilterChange("location", e.target.value)}
                                    >
                                        <MenuItem value="">All Locations</MenuItem>
                                        {locationOptions.map((location) => (
                                            <MenuItem key={location} value={location}>
                                                {location}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Autocomplete
                                    size="small"
                                    options={filteredEmployees}
                                    loading={loadingFilters}
                                    value={filters.employee}
                                    getOptionLabel={(option) => {
                                        if (!option) return "";
                                        const name = `${option.first_name || ""} ${option.last_name || ""}`.trim();
                                        const empId = option.emp_id ? ` (${option.emp_id})` : "";
                                        return `${name}${empId}`.trim();
                                    }}
                                    onChange={(_, value) => handleFilterChange("employee", value)}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Employee" placeholder="All Employees" />
                                    )}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Payroll Batch</InputLabel>
                                    <Select
                                        label="Payroll Batch"
                                        value={filters.payrollBatch}
                                        onChange={(e) => handleFilterChange("payrollBatch", e.target.value)}
                                    >
                                        <MenuItem value="">All Batches</MenuItem>
                                        {payrollBatches.map((batch) => (
                                            <MenuItem key={batch.value} value={batch.value}>
                                                {batch.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    ) : (
                        <Box sx={{ mt: 3, border: "1px solid #e2e8f0", borderRadius: 2, p: 2, bgcolor: "#f8fafc" }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                    gap: 1,
                                    mb: 1.5,
                                }}
                            >
                                <Typography variant="subtitle1" fontWeight={700}>
                                    Preview ({previewRows.length} rows)
                                </Typography>
                                <Box sx={{ display: "flex", gap: 1 }}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<TableViewOutlinedIcon />}
                                        disabled={!!activeAction}
                                        onClick={() => runReportAction("excel")}
                                    >
                                        {activeAction === "excel" ? "Downloading..." : "Download Excel"}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<PictureAsPdfOutlinedIcon />}
                                        disabled={!!activeAction}
                                        onClick={() => runReportAction("pdf")}
                                    >
                                        {activeAction === "pdf" ? "Downloading..." : "Download PDF"}
                                    </Button>
                                </Box>
                            </Box>

                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Box sx={{ p: 1.5, border: "1px solid #e2e8f0", borderRadius: 1.5, bgcolor: "#fff" }}>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                                            Meta
                                        </Typography>
                                        {Object.keys(previewMeta).length === 0 ? (
                                            <Typography variant="body2" color="text.secondary">
                                                No meta available.
                                            </Typography>
                                        ) : (
                                            Object.entries(previewMeta).map(([key, value]) => (
                                                <Typography key={`meta-${key}`} variant="body2" sx={{ mb: 0.5 }}>
                                                    <b>{key}:</b> {formatPreviewValue(value)}
                                                </Typography>
                                            ))
                                        )}
                                    </Box>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Box sx={{ p: 1.5, border: "1px solid #e2e8f0", borderRadius: 1.5, bgcolor: "#fff" }}>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                                            Totals
                                        </Typography>
                                        {Object.keys(previewTotals).length === 0 ? (
                                            <Typography variant="body2" color="text.secondary">
                                                No totals available.
                                            </Typography>
                                        ) : (
                                            Object.entries(previewTotals).map(([key, value]) => (
                                                <Typography key={`total-${key}`} variant="body2" sx={{ mb: 0.5 }}>
                                                    <b>{key}:</b> {formatPreviewValue(value)}
                                                </Typography>
                                            ))
                                        )}
                                    </Box>
                                </Grid>
                            </Grid>

                            <Divider sx={{ mb: 1.5 }} />

                            <Box
                                sx={{
                                    maxHeight: 320,
                                    overflow: "auto",
                                    border: "1px solid #cbd5e1",
                                    borderRadius: 1.5,
                                    p: 1.5,
                                    bgcolor: "#fff",
                                }}
                            >
                                {previewRows.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        No data available for selected filters.
                                    </Typography>
                                ) : (
                                    <TableContainer>
                                        <Table
                                            size="small"
                                            stickyHeader
                                            sx={{
                                                tableLayout: "auto",
                                                "& .MuiTableCell-root": {
                                                    borderColor: "#e2e8f0",
                                                    verticalAlign: "top",
                                                },
                                            }}
                                        >
                                            <TableHead>
                                                <TableRow>
                                                    {previewColumns.map((column) => (
                                                        <TableCell
                                                            key={`col-${column}`}
                                                            sx={{
                                                                fontWeight: 700,
                                                                bgcolor: "#f1f5f9",
                                                                color: "#0f172a",
                                                                whiteSpace: "nowrap",
                                                            }}
                                                        >
                                                            {column}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {previewRows.map((row, rowIndex) => (
                                                    <TableRow
                                                        key={`row-${rowIndex}`}
                                                        sx={{
                                                            "&:nth-of-type(even)": { bgcolor: "#f8fafc" },
                                                            "&:hover": { bgcolor: "#eef2ff" },
                                                        }}
                                                    >
                                                        {previewColumns.map((column) => (
                                                            <TableCell
                                                                key={`cell-${rowIndex}-${column}`}
                                                                sx={{
                                                                    fontSize: 12,
                                                                    maxWidth: 240,
                                                                    wordBreak: "break-word",
                                                                }}
                                                            >
                                                                {formatPreviewValue(row?.[column])}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                                {previewRows.length > 20 && (
                                    <Typography variant="caption" color="text.secondary">
                                        Showing {previewRows.length} rows.
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
                    <Button onClick={closeFilterDialog}>Cancel</Button>
                    {!previewData && (
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
                            <Button
                                variant="contained"
                                startIcon={<AssessmentOutlinedIcon />}
                                disabled={!!activeAction}
                                onClick={() => runReportAction("preview")}
                            >
                                {activeAction === "preview" ? "Loading Preview..." : "Preview"}
                            </Button>
                        </Box>
                    )}
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default PayslipReportsTab;